import React, { useContext, useRef, useState } from 'react';
import { CatalogCarrierFilter, CatalogCarrierPreviewOutDTO, Pageable } from '@api/ringil-api.v2';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import styled from 'styled-components';
import { useCountryRegion, useImportExportCountries, useLovTruckTypes } from '@hooks/customer.hooks.tsx';
import AddCarrierSidebar from './AddCarrierSidebar';
import { ApiContext } from '@api/api';
import useNavigation from '@hooks/useNavigation.tsx';
import { Link } from 'react-router-dom';
import { CarrierReferrer } from '@app/pages/customer/carriers/carrier-detail/CarrierDetail.tsx';
import clsx from 'clsx';
import BreadCrumbComponent from '@components/obsolete/BreadCrumbComponent/BreadCrumbComponent.tsx';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import { useMediaQuery } from 'react-responsive';
import RingilDataTable, {
  ColumnConfig,
  FilterConfig,
  FilterType,
  PagedData,
} from '@components/RingilDataTable/RingilDataTable.tsx';
import CustomTooltip from '@components/ringil3/Tooltip/CustomTooltip.tsx';
import SvgBook from '@icons/book.svg?react';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import COTimezone from '@components/FigmaComponents/COTimezone.tsx';

interface ComponentProps {
}

const CarrierCatalog: React.FC<ComponentProps> = () => {
  const { cuGetCatalogCarriers, cuDeleteCarrier } = useContext(ApiContext);
  const { tr } = useTranslationLgs();
  const countryRegions = useCountryRegion();
  const importExportCountries = useImportExportCountries();
  const truckTypes = useLovTruckTypes();
  const [addCarrierId, setAddCarrierId] = useState(undefined);
  const navigation = useNavigation();
  const isMobile = useMediaQuery({ maxWidth: 1024 });

  const loadDataApi = (filter: CatalogCarrierFilter, pageable: Pageable, onSuccess: (pagedData: PagedData<CatalogCarrierPreviewOutDTO>) => void) => {
    cuGetCatalogCarriers(
      filter,
      pageable,
      data => {
        onSuccess(data);
      },
    );
  };

  const renderIdColumn = (data: CatalogCarrierPreviewOutDTO) => {
    return (
      <div className={'flex flex-col items-start gap-2'}>
        <Link
          to={navigation.createNavigationLink(navigation.urlFunctions.createCarrierDetail(data.carrierId, CarrierReferrer.CARRIER_CATALOG))}
        >
            <span
              className={'text-[16px] font-normal leading-normal tracking-[0.24px] text-content-brand font-dm-sans'}
            >
              {data.name}
            </span>
        </Link>
      </div>
    );
  };

  const renderCountryRegion = (data: CatalogCarrierPreviewOutDTO) => {
    return (
      <div className={'flex flex-col items-start gap-2'}>
        <span>
          {data.countryRegion}
        </span>
        <COTimezone timezoneId={data.timezoneId} />
      </div>
    );
  };

  const renderExportCountries = (row: CatalogCarrierPreviewOutDTO) => {
    return (
      <div className={'flex flex-row items-center justify-between gap-2'}>
        <div>{row.exportCountries.join(', ')}</div>
        {row.exportNote && (
          <div className="flex-shrink-0 w-4 h-4">
            <SvgBook id={`exportNote${row.carrierId}`} />
            <CustomTooltip targetSelector={`#exportNote${row.carrierId}`} content={row.exportNote} />
          </div>
        )}
      </div>
    );
  };

  const renderImportCountries = (row: CatalogCarrierPreviewOutDTO) => {
    return (
      <div className={'flex flex-row items-center justify-between gap-2'}>
        <div>{row.importCountries.join(', ')}</div>
        {row.importNote && (
          <div className="flex-shrink-0 w-4 h-4">
            <SvgBook id={`importNote${row.carrierId}`} />
            <CustomTooltip targetSelector={`#importNote${row.carrierId}`} content={row.importNote} />
          </div>
        )}
      </div>
    );
  };

  const renderTrucks = (row: CatalogCarrierPreviewOutDTO) => {
    return (
      <div className={'flex flex-col items-start gap-2'}>
        <span>
          {row.cars.map(car => `${car.name} (${car.count})`).join(', ')}
        </span>
      </div>
    );
  };

  const renderActions = (row: CatalogCarrierPreviewOutDTO) => {
    return (
      <ActionButtons>
        {!row.isAlreadyAdded && (
          <RingilButton
            mode={'table'}
            label={tr(`CarrierCatalog.addCarrier`, `Add carrier`)}
            onClick={() => setAddCarrierId(row.carrierId)}
          />
        )}
        {row.isAlreadyAdded && (
          <RingilButton
            mode={'table'}
            label={tr(`CarrierCatalog.removeCarrier`, `Remove carrier`)}
            onClick={() => {
              cuDeleteCarrier(
                row.carrierId,
                () => {
                  tableRef?.current?.reloadData();
                },
              );
            }}
          />
        )}
      </ActionButtons>
    );
  };

  const columnsConfig: ColumnConfig<CatalogCarrierPreviewOutDTO>[] = [
    {
      id: 'name',
      header: 'CarrierCatalog.columnHeaderName',
      headerDefault: 'Name',
      width: '18%',
      sortable: false,
      field: 'name',
      order: 1,
      render: renderIdColumn,
    },
    {
      id: 'countryRegion',
      header: 'CarrierCatalog.columnHeaderCountryRegion',
      headerDefault: 'Country Region',
      width: '18%',
      sortable: false,
      field: 'countryRegion',
      order: 2,
      render: renderCountryRegion,
    },
    {
      id: 'countryExport',
      header: 'CarrierCatalog.columnHeaderCountryExport',
      headerDefault: 'Export country',
      width: '18%',
      sortable: false,
      field: 'exportCountries',
      order: 3,
      render: renderExportCountries,
    },
    {
      id: 'countryImport',
      header: 'CarrierCatalog.columnHeaderCountryImport',
      headerDefault: 'Import country',
      width: '18%',
      sortable: false,
      field: 'importCountries',
      order: 4,
      render: renderImportCountries,
    },
    {
      id: 'trucks',
      header: 'CarrierCatalog.columnHeaderTrucks',
      headerDefault: 'Trucks',
      width: '18%',
      sortable: false,
      field: 'cars',
      order: 5,
      render: renderTrucks,
    },
    {
      id: 'actions',
      width: '10%',
      sortable: false,
      field: 'cars',
      order: 6,
      render: renderActions,
    },
  ];

  const filtersConf: FilterConfig[] = [
    {
      filterType: FilterType.InputText,
      placeholder: tr(`CarrierCatalog.filterCarrierName`, `Carrier name`),
      value: ['carrierName'],
    },
    {
      filterType: FilterType.MultiSelect,
      placeholder: tr(`CarrierCatalog.filterCountryRegion`, `Country region`),
      value: ['headquarters'],
      options: countryRegions.map(countryRegion => ({
        label: countryRegion.name,
        value: countryRegion.id,
        active: countryRegion.active,
      })),
    },
    {
      filterType: FilterType.MultiSelect,
      placeholder: tr(`CarrierCatalog.filterExportCountries`, `Export countries`),
      value: ['exportCountries'],
      options: importExportCountries.map(exportCountry => ({
        label: exportCountry.name,
        value: exportCountry.code,
        active: true,
      })),
    },
    {
      filterType: FilterType.MultiSelect,
      placeholder: tr(`CarrierCatalog.filterImportCountries`, `Import countries`),
      value: ['importCountries'],
      options: importExportCountries.map(exportCountry => ({
        label: exportCountry.name,
        value: exportCountry.code,
        active: true,
      })),
    },
    {
      filterType: FilterType.MultiSelect,
      placeholder: tr(`CarrierCatalog.filterTrucks`, `Trucks`),
      value: ['trucks'],
      options: truckTypes.map(truck => ({ label: truck.name, value: truck.code, active: true })),
    },
  ];

  const tableRef = useRef<{ reloadData: () => void; selectedObjects: unknown[]; clear: () => void }>(null);

  return (
    <>
      <div className={clsx('font-[\'DM Sans\']', isMobile && '-mx-4 px-8 pt-8')}>
        <BreadCrumbComponent items={[{ label: tr(`CarrierCatalog.carrierCatalogTitle`, `Carrier catalogue`) }]} />
        <div className="flex items-center mb-6">
          <COHeadline title={tr(`CarrierCatalog.carrierCatalogTitle`, `Carrier catalogue`)} variant={'h1'} />
        </div>
      </div>

      <RingilDataTable
        ref={tableRef}
        columnConfig={columnsConfig as ColumnConfig<unknown>[]}
        filtersConf={filtersConf}
        loadDataFromApi={loadDataApi}
        filterName={'carrier_catalogue_list'}
      />

      <AddCarrierSidebar
        visible={addCarrierId}
        onHide={() => setAddCarrierId(undefined)}
        carrierId={addCarrierId}
        onAdded={() => {
          setAddCarrierId(undefined);
          tableRef?.current?.reloadData();
        }}
      />

    </>
  );
};

const FlexContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StyledDataTable = styled.div`
  .p-datatable .p-datatable-tbody > tr > td {
    cursor: auto;
    padding: 1rem 1rem;
  }
`;

const ActionButtons = styled.div`
  display: flex;
`;

const ListAndNote = styled.div`
  display: flex;
  justify-content: space-between;
`;
export default CarrierCatalog;
